import { Component, OnInit } from '@angular/core';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { Router, RouterModule } from '@angular/router';
import { CommonModule, CurrencyPipe, DatePipe, formatDate } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTooltip } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { BehaviorSubject } from 'rxjs';
import { EditImportDataRowDialogComponent } from './components/edit-import-data-row-dialog/edit-import-data-row-dialog.component';
import { DeleteImportDataRowDialogComponent } from './components/delete-import-data-row-dialog/delete-import-data-row-dialog.component';
import { SearchService } from '../../../../shared/search/search.service';
import { PropertyReferenceIds, Reservation } from '../../models/Reservation';
import { ReservationService } from '../../services/reservation.service';
import { BackButtonComponent } from '../../../../shared/components/back-button/back-button.component';
import { Logger } from '../../../../infrastructure/logging/logger';
import { Currency } from '../../../currencies/models/Currency';
import { CurrenciesService } from '../../../currencies/services/currencies.service';
import { PropertyApiService } from '../../../properties/services/property.api.service';

@Component({
  selector: 'app-review-reservation-data',
  templateUrl: './review-reservation-data.component.html',
  styleUrls: ['./review-reservation-data.component.scss'],
  standalone: true,
  imports: [
    MatIcon,
    MatCardModule,
    MatProgressSpinner,
    MatTableModule,
    DatePipe,
    MatTooltip,
    CurrencyPipe,
    CommonModule,
    MatButtonModule,
    RouterModule,
    BackButtonComponent
  ]
})
export class ReviewReservationDataComponent implements OnInit {

  private static readonly REQUIRED_DATE_FIELDS = ['Accommodation Start Date', 'Accommodation End Date'];

  loading = new BehaviorSubject<boolean>(false);
  dataSource = new MatTableDataSource<any>();
  displayedColumns = [ 'booking_id', 'policy_id', 'organization_id', 'unit_id', 'property_name', 'booking_date', 'accommodation_start_date', 'accommodation_end_date', 'reservation_cost', 'guest_name', 'owner_name', 'created_date', 'note' ];
  
  organizationId: number = 0;
  currencies!: Currency[];
  reservations!: Reservation[];
  reservationImportData: any;
  importDataPayloadArray: any = [];
  createReservationPayloadArray: any = [];
  fullImportDataPayload: any;

  propertyReferenceIds!: PropertyReferenceIds;
  matchingOrganizationProperties: any;
  createdDate!: string;
  validData: boolean = true;

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private reservationService: ReservationService,
    private searchService: SearchService,
    private currenciesService: CurrenciesService,
    private propertyService: PropertyApiService,
    private logger: Logger
  ) {
    this.organizationId = this.router.getCurrentNavigation()?.extras.state?.['organizationId'];
    this.createdDate = this.router.getCurrentNavigation()?.extras.state?.['createdDate'];
    this.reservationImportData = this.router.getCurrentNavigation()?.extras.state?.['importData'];
    this.dataSource.data = this.reservationImportData;
  }

  ngOnInit() {
    this.loadPage();
  }

  async loadPage() {
    const currenciesResponse = await this.currenciesService.getCurrencies();
    this.currencies = currenciesResponse;

    if (!this.reservationImportData) {
      this.logger.handleError('Reservation data does not exist', 'An error occurred while retrieving Reservation data', 'Error while retrieving Reservation data');
    } else {
      this.getReservations();
    }
  }

  async getReservations() {
    this.searchService.resetQuery();

    try {
      this.loading.next(true);

      let params = {
        page: 1,
        pageSize: 1500,
        organizationId: this.organizationId
      }
      const reservations = this.reservationService.getLegacyReservations(params);
      this.reservations = (await reservations).reservations;

      await this.checkForReservationReferenceIdFlag();
      await this.checkForPropertyReferenceIdFlag();
      this.checkForMissingDateFlag();
    } catch (e) {
      this.logger.handleError('Reservation data does not exist', 'An error occurred while retrieving Reservation data', 'Error while retrieving Reservation data');
    } finally {
      this.loading.next(false);
    }
  }

  async checkForReservationReferenceIdFlag() {
    this.dataSource.data.forEach(importData => {
      if (this.reservations.some((reservation: any) => reservation.reference_id === importData['Booking ID'].toString())) {
        importData.showReservationFlag = true;
      } else {
        importData.showReservationFlag = false;
      }
    });
    this.updateDataSource();
  }

  async checkForPropertyReferenceIdFlag() {
    const organizationProperties = await this.propertyService.getPropertiesByOrganizationId(this.organizationId);

    let propertyReferenceIds: any = [];
    this.dataSource.data.forEach(reservationInfo => {
      propertyReferenceIds.push(reservationInfo['Unit ID'].toString());
    });
    let filteredReferenceIds = propertyReferenceIds.filter((value: any, index: any) => propertyReferenceIds.indexOf(value) === index);

    this.matchingOrganizationProperties = organizationProperties.filter(orgProperty => filteredReferenceIds.some((referenceId:any) => orgProperty.reference_id === referenceId));
    const nonMatchingOrgPropertyRefIds = this.dataSource.data.filter(resInfo => !this.matchingOrganizationProperties.some((prop: any) => resInfo['Unit ID'].toString() === prop.reference_id));
    nonMatchingOrgPropertyRefIds.forEach(reservationData => {
      reservationData.showPropertyFlag = true;
    });
  }

  checkForMissingDateFlag() {
    this.dataSource.data.forEach(importData => {
      importData.missingDateFields = ReviewReservationDataComponent.REQUIRED_DATE_FIELDS.filter(field => !importData[field]);
      importData.showMissingDateFlag = importData.missingDateFields.length > 0;
    });
    this.updateDataSource();
  }

  editImportDataRow(row: any) {
    this.openEditDataRowDialog(row);
  }

  openEditDataRowDialog(row: any) {
    const dialogRef = this.dialog.open(EditImportDataRowDialogComponent, {
      width: "60%",
      data: row,
      disableClose: true
    });
    dialogRef.afterClosed().subscribe(async data => {
      if (data) {
        this.checkForMissingDateFlag();
      }
    });
  }

  deleteImportDataRow(index: number, row: any) {
    this.openDeleteDataRowDialog(index, row);
  }

  openDeleteDataRowDialog(index: number, row: any) {
    const dialogRef = this.dialog.open(DeleteImportDataRowDialogComponent, {
      disableClose: true,
      data: row,
      width: '50%'
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.reservationImportData.splice(index, 1);
        this.updateDataSource();
      }
    });
  }

  updateDataSource() {
    let reservationReferenceIdCheck = this.dataSource.data.some((res: any) => res.showReservationFlag === true);
    let propertyReferenceIdCheck = this.dataSource.data.some((res: any) => res.showPropertyFlag === true);
    let missingDateCheck = this.dataSource.data.some((res: any) => res.showMissingDateFlag === true);

    if (!reservationReferenceIdCheck && !propertyReferenceIdCheck && !missingDateCheck) {
      this.validData = true;
    } else {
      this.validData = false;
    }
    this.dataSource.data = this.reservationImportData;
  }

  deleteAllImportData() {
    this.openConfirmDeleteAllImportDataDialog();
  }

  openConfirmDeleteAllImportDataDialog() {
    const dialogRef = this.dialog.open(DeleteImportDataRowDialogComponent, {
      disableClose: true
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.reservationImportData = [];
        this.updateDataSource();
        this.router.navigate(['reservations/import']);
      }
    });
  }

  async importAllData() {
    try {
      this.loading.next(true);
      this.importDataPayloadArray = [];
      this.createReservationPayloadArray = [];

      for (const reservation of this.reservationImportData) {
        this.importDataPayloadArray.push(this.buildImportData(reservation));
        this.createReservationPayloadArray.push(this.buildReservation(reservation));
      }

      this.fullImportDataPayload = {
        "importDataPayload": this.importDataPayloadArray,
        "createReservationPayload": this.createReservationPayloadArray
      }

      const saveResult = await this.reservationService.saveReservationImportData(this.fullImportDataPayload);
      if (saveResult.failedReservationsLength ?? saveResult.failedReservations?.length) {
        return;
      }

      this.router.navigate(['/reservations']);
    } catch(e) {
      this.logger.handleError(e, 'An error occurred while preparing Reservation create payload', 'Error occured preparing Reservation create payload');
    } finally {
      this.loading.next(false);
    }
  }

  buildImportData(res: any): any {
    let differenceInTime = new Date(formatDate(res["Accommodation End Date"], 'MM/dd/yyyy', 'en-US', 'UTC')).getTime() - new Date(formatDate(res["Accommodation Start Date"], 'MM/dd/yyyy', 'en-US', 'UTC')).getTime();
    let differenceInDays = Math.round(differenceInTime / (1000 * 3600 *24)) + 1;
    let avgNightCharge = Number(res["Total Charge"]) / differenceInDays;

    let guestFirstName = res["Guest's Name"].substring(0, res["Guest's Name"].indexOf(' '));
    let guestLastName = res["Guest's Name"].substring(res["Guest's Name"].indexOf(' ') + 1);

    return {
      reservationReferenceId: res["Booking ID"],
      policyId: res["Policy ID"],
      accommodationStartDate: new Date(formatDate(res["Accommodation Start Date"], 'MM/dd/yyyy', 'en-US', 'UTC')),
      accommodationEndDate: new Date(formatDate(res["Accommodation End Date"], 'MM/dd/yyyy', 'en-US', 'UTC')),
      reservationCost: res["Total Charge"],
      averageNightlyCharge: avgNightCharge,
      guestName: res["Guest's Name"],
      guestFirstName: guestFirstName,
      guestLastName: guestLastName,
      ownerName: res["Owners Name"],
      propertyReferenceId: res["Unit ID"],
      propertyName: res["Property Name"],
      propertyAddress: res["Property Address"],
      city: res["City"],
      state: res["State"],
      postalCode: res["Postal Code"],
      bookingDate: res["Reported"] ? new Date(formatDate(res["Reported"], 'MM/dd/yyyy', 'en-US', 'UTC')) : '',
      organizationId: this.organizationId,
      createdDate: new Date(formatDate(this.createdDate, 'MM/dd/yyyy', 'en-US', 'UTC')),
      updatedDate: new Date(),
      lastSyncDate: new Date()
    }
  }

  buildReservation(reservation: any): Reservation {
    const matchingProperty = this.matchingOrganizationProperties.find((prop: any) => prop.reference_id === reservation["Unit ID"].toString());
    if (!matchingProperty) {
      throw new Error(`No matching property found for Unit ID "${reservation["Unit ID"]}" (Booking ID ${reservation["Booking ID"]})`);
    }

    const timeDiff = Math.abs(new Date(reservation["Accommodation End Date"]).getTime() - new Date(reservation["Accommodation Start Date"]).getTime());

    return {
      id: 0,
      reservation_id: 0,
      reservation_reference_id: reservation["Booking ID"],
      reference_id: '',
      booking_date: reservation["Reported"] ? new Date(reservation["Reported"]).toISOString() : '',
      arrival_date: new Date(reservation["Accommodation Start Date"]).toISOString(),
      departure_date: new Date(reservation["Accommodation End Date"]).toISOString(),
      insurance_cost: reservation["Premium Due"] ? reservation["Premium Due"] : 0,
      insurance_refund_amount: 0,
      insurance_refunded: false,
      number_of_guests: 1,
      number_of_nights: Math.ceil(timeDiff / (1000 * 3600 * 24)),
      reservation_cost: reservation["Total Charge"] ? reservation["Total Charge"] : 0,
      status: "UPCOMING",
      reservation_currency_id: 1,
      updated_insurance_cost: reservation["Premium Due"] ? reservation["Premium Due"] : 0,
      exclude_from_invoice: false,
      verification_cost: 0,
      updated_verification_cost: 0,
      verification_paid: false,
      status_id: 1,
      status_history: [],
      claim_token: '',
      claim_count: 0,
      score: 0,
      rating: '0',
      property_id: matchingProperty.property_id ? matchingProperty.property_id : 0,
      property: matchingProperty,
      organization_id: this.organizationId.toString(),
      reservation_currency: this.currencies.find(currency => currency.id === 1)!,
      for_guest_verification: 1,
      for_reservation: 1,
      selected_policies: reservation["Policy ID"] ? [ reservation["Policy ID"] ] : [],
      reservation_guests: [{
        first_name: reservation["Guest's Name"].substring(0, reservation["Guest's Name"].indexOf(' ')),
        middle_name: "",
        last_name: reservation["Guest's Name"].substring(reservation["Guest's Name"].indexOf(' ') + 1),
        birth_date: undefined,
        email: "",
        phone_number: "",
        address: {
          address_line_1: "",
          address_line_2: "",
          city: "",
          state: "",
          postal_code: "",
          country_id: 0,
          country_name: ""
        },
        verification_cost: 0
      }],
      guests: [{
        guest_id: 0,
        first_name: reservation["Guest's Name"].substring(0, reservation["Guest's Name"].indexOf(' ')),
        middle_name: "",
        last_name: reservation["Guest's Name"].substring(reservation["Guest's Name"].indexOf(' ') + 1),
        birth_date: undefined,
        email: "",
        phone_number: "",
        address: {
          address_line_1: "",
          address_line_2: "",
          city: "",
          state: "",
          postal_code: "",
          country_id: 0,
          country_name: ""
        },
        verification_cost: 0
      }]
    }
  }
}